/**
 * Context Page - Business context entry form
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import contextService from '../services/contextService';

const ContextPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [description, setDescription] = useState('');
  const [objectives, setObjectives] = useState(['']);
  const [timeline, setTimeline] = useState('2-year');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddObjective = () => {
    setObjectives([...objectives, '']);
  };

  const handleObjectiveChange = (index: number, value: string) => {
    const newObjectives = [...objectives];
    newObjectives[index] = value;
    setObjectives(newObjectives);
  };

  const handleRemoveObjective = (index: number) => {
    if (objectives.length > 1) {
      setObjectives(objectives.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validation
    if (description.length < 50) {
      setError('Business description must be at least 50 characters');
      setIsSubmitting(false);
      return;
    }

    const filteredObjectives = objectives.filter(obj => obj.trim() !== '');
    if (filteredObjectives.length === 0) {
      setError('At least one objective is required');
      setIsSubmitting(false);
      return;
    }

    try {
      await contextService.submitContext(sessionId!, {
        business_description: description,
        objectives: filteredObjectives,
        timeline: timeline,
      });

      // Navigate to agents page
      navigate(`/agents/${sessionId}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit context');
      setIsSubmitting(false);
    }
  };

  const charCount = description.length;
  const isDescriptionValid = charCount >= 50;

  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
      <header style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Business Context</h1>
        <p style={{ color: '#666' }}>
          Tell us about your business so we can select the most relevant specialist agents
        </p>
      </header>

      <form onSubmit={handleSubmit}>
        {/* Business Description */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
            Business Description *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your business, products/services, target market, and current situation..."
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '12px',
              fontSize: '1rem',
              border: `2px solid ${isDescriptionValid ? '#28a745' : '#ccc'}`,
              borderRadius: '4px',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '0.9rem', color: isDescriptionValid ? '#28a745' : '#999' }}>
              {charCount}/50 characters minimum
            </span>
            {!isDescriptionValid && charCount > 0 && (
              <span style={{ fontSize: '0.9rem', color: '#dc3545' }}>
                {50 - charCount} more characters needed
              </span>
            )}
          </div>
        </div>

        {/* Objectives */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
            Business Objectives *
          </label>
          {objectives.map((objective, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={objective}
                onChange={(e) => handleObjectiveChange(index, e.target.value)}
                placeholder={`Objective ${index + 1} (e.g., "Expand market reach")`}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '1rem',
                  border: '2px solid #ccc',
                  borderRadius: '4px',
                }}
              />
              {objectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveObjective(index)}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddObjective}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            + Add Another Objective
          </button>
        </div>

        {/* Timeline */}
        <div style={{ marginBottom: '32px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
            Timeline *
          </label>
          <select
            value={timeline}
            onChange={(e) => setTimeline(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '1rem',
              border: '2px solid #ccc',
              borderRadius: '4px',
            }}
          >
            <option value="2-year">2-Year Plan</option>
            <option value="3-year">3-Year Plan</option>
            <option value="5-year">5-Year Plan</option>
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '14px',
              fontSize: '1.1rem',
              backgroundColor: isSubmitting ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
            }}
          >
            {isSubmitting ? 'Selecting Agents...' : 'Continue to Agent Selection'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContextPage;
